import { TEKS_MODUL_BELUM_DIPILIH } from "@/lib/akunAdmin";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText } from "lucide-react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatFullWIB } from "@/lib/datetime";
import { ROLE_BADGE_CLASS, ROLE_LABEL, istilahProdi } from "@/lib/roles";
import {
    LABEL_TAB_KEAKTIFAN, NAMA_BULAN, PILIHAN_JENJANG, STATUS_KEAKTIFAN, TAB_KEAKTIFAN, bulanIso, daftarBulanDiTahun, daftarTahun, isoSetelahGantiTahun, keteranganAmbang, tabKeaktifanDariUrl,
    teksPersen, teksRelatifHari,
} from "@/lib/keaktifan";

// Indikator Keaktifan Akun (Super Admin & Admin UFP). Data dari GET /admin/keaktifan; status & hari dihitung server.
// Warna selalu berpasangan dgn teks status (aksesibilitas). Tidak memuat data peserta sama sekali.

// Sel "Terakhir login": titik warna + teks status + waktu relatif; tooltip = tanggal lengkap WIB.
// `relative` wajib: tanpa itu span sr-only (position:absolute) mengacu ke halaman & memanjangkan dokumen,
// sehingga jendela ikut tergulir dan judul/tab yang menempel (StickyHeader) ikut hilang.
export function SelTerakhirLogin({ akun }) {
    const st = STATUS_KEAKTIFAN[akun?.status] || STATUS_KEAKTIFAN.abu;
    const lengkap = akun?.terakhir_login ? `${formatFullWIB(akun.terakhir_login)} WIB` : "Belum ada catatan login";
    return (
        <span className="relative inline-flex items-center gap-1.5 text-xs whitespace-nowrap" title={lengkap} data-testid="keaktifan-login">
            <span aria-hidden="true" className={`inline-block w-2.5 h-2.5 rounded-full ${st.kelas}`} />
            <span className="font-semibold text-slate-700">{st.label}</span>
            {akun?.terakhir_login && <span className="text-slate-500">· {teksRelatifHari(akun.hari_sejak_login)}</span>}
            <span className="sr-only">, terakhir login {lengkap}</span>
        </span>
    );
}

export function KeteranganStatus({ ambang }) {
    const ket = keteranganAmbang(ambang);
    return (
        <div className="space-y-0.5" data-testid="keaktifan-keterangan">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                <span>Sejak login terakhir:</span>
                {Object.entries(STATUS_KEAKTIFAN).map(([k, st]) => (
                    <span key={k} className="inline-flex items-center gap-1">
                        <span aria-hidden="true" className={`inline-block w-2 h-2 rounded-full ${st.kelas}`} />
                        {st.label}{k !== "abu" && ket[k] ? ` (${ket[k]})` : ""}
                    </span>
                ))}
            </div>
            <p className="text-[11px] text-slate-500" data-testid="keaktifan-keterangan-abu">
                "Belum pernah login" berarti belum ada catatan login di Audit Log untuk akun itu — bukan berarti akunnya
                nonaktif (status aktif/nonaktif akun ditandai terpisah).
            </p>
        </div>
    );
}

// `onReset` (opsional, hanya halaman /keaktifan utk Admin UFP): tombol "Reset Kata Sandi" pada baris yg server
// tandai `boleh_direset`. Tanpa `onReset`, tabel sama persis spt sebelumnya (tanpa kolom Aksi).
export function TabelAkunKeaktifan({ akun, denganEmail, onReset }) {
    const kolom = (denganEmail ? 5 : 4) + (onReset ? 1 : 0);
    return (
        <div className="overflow-x-auto">
            <table className="w-full compact" data-testid="keaktifan-tabel-akun">
                <thead className="bg-slate-100">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                        <th>Nama</th>{denganEmail && <th>Email</th>}<th>Peran</th><th>Prodi / Modul / Instalasi</th><th>Terakhir login</th>
                        {onReset && <th className="text-right pr-4">Aksi</th>}
                    </tr>
                </thead>
                <tbody>
                    {akun.length === 0 && <tr><td colSpan={kolom} className="text-center py-8 text-slate-400">Belum ada akun.</td></tr>}
                    {akun.map((a) => (
                        <tr key={a.id} className="border-t border-slate-200" data-testid={`keaktifan-akun-${a.id}`}>
                            <td className="font-semibold text-slate-800">{a.nama}{!a.aktif && <span className="ml-1 text-[10px] text-slate-500">(nonaktif)</span>}</td>
                            {denganEmail && <td className="font-mono text-xs">{a.email}</td>}
                            <td>
                                {ROLE_BADGE_CLASS[a.peran]
                                    ? <Badge className={ROLE_BADGE_CLASS[a.peran]}>{ROLE_LABEL[a.peran]}</Badge>
                                    : <Badge variant="outline">{ROLE_LABEL[a.peran] || a.peran}</Badge>}
                            </td>
                            <td className="text-xs text-slate-600">{a.prodi || (a.instalasi?.length ? a.instalasi.join(", ") : a.peran === "admin_koas" ? TEKS_MODUL_BELUM_DIPILIH : "-")}</td>
                            <td><SelTerakhirLogin akun={a} /></td>
                            {onReset && (
                                <td className="text-right pr-2">
                                    {a.boleh_direset && (
                                        <Button size="sm" variant="outline" onClick={() => onReset(a)}
                                            data-testid={`keaktifan-reset-${a.id}`}>
                                            Reset Kata Sandi
                                        </Button>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Pemilih Tahun + Bulan + Jenjang + unduh Excel/PDF Ringkasan per Program Studi (/keaktifan). Tahun & Bulan diturunkan dari
// `pilihan_bulan` server (satu rentang sah); file dibuat server utk bulan terpilih dgn kolom yg sama dgn tabel di bawahnya.
export function AlatRingkasanProdi({ pilihan, bulan, onBulan, jenjang = "semua", onJenjang, onUnduh, mengunduh, memuat }) {
    const [tahunIso, bulanIsoTerpilih] = (bulan || "").split("-");
    const tahun = Number(tahunIso);
    const nomorBulan = Number(bulanIsoTerpilih);
    const gantiTahun = (nilai) => {
        const iso = isoSetelahGantiTahun(pilihan, nilai, nomorBulan);
        if (iso) onBulan(iso);
    };
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="keaktifan-alat-prodi">
            <Select value={String(tahun)} onValueChange={gantiTahun} disabled={memuat}>
                <SelectTrigger className="w-28 h-9 bg-white" aria-label="Pilih tahun" data-testid="keaktifan-tahun-select">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {daftarTahun(pilihan).map((t) => <SelectItem key={t} value={String(t)}>{t}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={String(nomorBulan)} onValueChange={(b) => onBulan(bulanIso(Number(b), tahun))} disabled={memuat}>
                <SelectTrigger className="w-36 h-9 bg-white" aria-label="Pilih bulan" data-testid="keaktifan-bulan-select">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {daftarBulanDiTahun(pilihan, tahun).map((b) => <SelectItem key={b} value={String(b)}>{NAMA_BULAN[b - 1]}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={jenjang} onValueChange={onJenjang} disabled={memuat}>
                <SelectTrigger className="w-32 h-9 bg-white" aria-label="Pilih jenjang" data-testid="keaktifan-jenjang-select">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {PILIHAN_JENJANG.map((j) => <SelectItem key={j.nilai} value={j.nilai}>{j.label}</SelectItem>)}
                </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9" data-testid="keaktifan-unduh-excel"
                onClick={() => onUnduh("excel")} disabled={!!mengunduh || memuat}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {mengunduh === "excel" ? "Menyiapkan..." : "Excel"}
            </Button>
            <Button variant="outline" size="sm" className="h-9" data-testid="keaktifan-unduh-pdf"
                onClick={() => onUnduh("pdf")} disabled={!!mengunduh || memuat}>
                <FileText className="w-4 h-4 mr-2" />
                {mengunduh === "pdf" ? "Menyiapkan..." : "PDF"}
            </Button>
        </div>
    );
}

// Ringkasan per prodi: terakhir menyimpan rotasi (nama akun, tanpa email) & terisi/wajib bulan dari server
// (bawaan bulan berjalan; di /keaktifan bisa dipilih) -- angka sama dgn Monitoring Rotasi Klinik.
// `kolomPertama`, `total` (baris total bagian jenjang dari server) & `testId` dipakai tampilan per jenjang di /keaktifan;
// tanpa ketiganya (tab Ringkasan di /users) tampilan tidak berubah.
export function RingkasanProdiKeaktifan({ prodi, bulan, tahun, kolomPertama = "Program Studi", total, testId = "keaktifan-tabel-prodi" }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full compact" data-testid={testId}>
                <thead className="bg-slate-100">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                        <th>{kolomPertama}</th><th>Terakhir simpan rotasi (keseluruhan)</th><th>Oleh</th>
                        <th>Terisi / wajib {NAMA_BULAN[bulan - 1]} {tahun}</th><th>%</th>
                    </tr>
                </thead>
                <tbody>
                    {prodi.map((p) => (
                        <tr key={p.prodi_id} className={`border-t border-slate-200 ${p.tanpa_kewajiban ? "text-slate-400" : ""}`}
                            data-testid={`keaktifan-prodi-${p.prodi_id}`}>
                            <td className={p.tanpa_kewajiban ? "font-semibold text-slate-400" : "font-semibold text-slate-800"}>{p.prodi}</td>
                            <td className="text-xs text-slate-600" title={p.terakhir_simpan_rotasi ? `${formatFullWIB(p.terakhir_simpan_rotasi)} WIB` : undefined}>
                                {p.terakhir_simpan_rotasi ? formatFullWIB(p.terakhir_simpan_rotasi) : <span className="text-slate-400">Belum pernah</span>}
                            </td>
                            <td className="text-xs text-slate-600">
                                {p.terakhir_simpan_oleh || "-"}
                                {p.terakhir_simpan_peran && <span className="text-slate-400"> ({ROLE_LABEL[p.terakhir_simpan_peran] || p.terakhir_simpan_peran})</span>}
                            </td>
                            {p.tanpa_kewajiban ? (
                                <td colSpan={2} className="text-xs text-slate-400" data-testid="keaktifan-tanpa-kewajiban">
                                    Tidak ada kewajiban isi pada bulan yang dipilih
                                </td>
                            ) : (
                                <>
                                    <td className="text-sm">{p.terisi} / {p.wajib}</td>
                                    <td className="text-sm font-semibold">{teksPersen(p.persen)}</td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
                {total && (
                    <tfoot>
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold text-slate-800" data-testid={`${testId}-total`}>
                            <td>{total.judul}</td><td /><td />
                            <td className="text-sm">{total.terisi} / {total.wajib}</td>
                            <td className="text-sm">{teksPersen(total.persen)}</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

// Ringkasan per jenjang (/keaktifan): satu bagian bertajuk per jenjang dari server (`bagian`), tiap bagian dgn baris
// total sendiri. Pada pilihan satu jenjang server hanya mengirim bagian itu. Jenjang Koas memakai istilah "Modul".
export function RingkasanProdiPerJenjang({ bagian, bulan, tahun }) {
    return (
        <div data-testid="keaktifan-tabel-prodi">
            {bagian.map((b) => (
                <section key={b.judul} className="border-t first:border-t-0 border-slate-200" data-testid={`keaktifan-bagian-${(b.jenjang || "lain").toLowerCase()}`}>
                    <h3 className="px-4 pt-3 pb-1 text-sm font-bold text-slate-700">{b.judul}</h3>
                    <RingkasanProdiKeaktifan prodi={b.prodi} bulan={bulan} tahun={tahun}
                        kolomPertama={b.jenjang === "Koas" ? istilahProdi("admin_koas").singkat : "Program Studi"}
                        total={{ ...b.total, judul: `Total ${b.judul}` }}
                        testId={`keaktifan-tabel-${(b.jenjang || "lain").toLowerCase()}`} />
                </section>
            ))}
        </div>
    );
}

// Tab terpilih dibaca/ditulis di ?tab= (tetap saat dimuat ulang, bisa dibagikan); parameter URL lain tidak
// disentuh. `replace` supaya berpindah tab tidak menumpuk riwayat tombol Kembali.
export function useTabKeaktifan(bawaan) {
    const [params, setParams] = useSearchParams();
    const tab = tabKeaktifanDariUrl(params.get("tab"), bawaan);
    const ubahTab = (nilai) => setParams((lama) => {
        const baru = new URLSearchParams(lama);
        baru.set("tab", nilai);
        return baru;
    }, { replace: true });
    return [tab, ubahTab];
}

// Daftar tab (Radix: role tablist/tab, panah kiri/kanan, Home/End). Dipasang di dalam <Tabs> milik halaman,
// di StickyHeader supaya tetap terjangkau tanpa menggulir; bergulir mendatar bila layar sempit.
export function DaftarTabKeaktifan() {
    return (
        <div className="overflow-x-auto -mx-1 px-1">
            <TabsList aria-label="Tampilan keaktifan" data-testid="keaktifan-tablist">
                {Object.values(TAB_KEAKTIFAN).map((t) => (
                    <TabsTrigger key={t} value={t} data-testid={`keaktifan-tab-${t}`}>{LABEL_TAB_KEAKTIFAN[t]}</TabsTrigger>
                ))}
            </TabsList>
        </div>
    );
}
